/** Links API shapes (crm/src/server/routes/trackingLinks.routes.js; ADR 0032, 0039). */

export type Earning = 'net' | 'gross'

/** A link is a tracking link or a trial link; the two are always shown apart. */
export type LinkKind = 'tracking' | 'trial'

/** A link's performance over a range (null = not known yet). */
export interface LinkPerformance {
  account_id: string
  kind: LinkKind
  link_id: string
  name: string
  profile_name: string | null
  profile_username: string | null
  created_at: string | null
  is_deleted: boolean
  archived: boolean
  clicks: number | null
  /** The range starts before hourly counts began: this is the lifetime count. */
  clicks_partial: boolean
  fans: number | null
  /** Fans is OnlyFans' own count: the claimers have not been read yet. */
  fans_from_onlyfans: boolean
  /** Of the fans, how many were already there when watching began (first seen = that day). */
  fans_backlog: number
  earnings: number | null
  cost: number
  profit: number | null
  cpf: number | null
  arpf: number | null
  /** Ratios, e.g. 0.25 = 25 %. */
  roi: number | null
  conversion: number | null
  cpc: number | null
  epc: number | null
  spenders: number | null
  /** Spenders as a share of every claimer of the link. */
  spenders_share: number | null
  /** Trial links: claimers who paid a subscription after their claim. */
  paid_after_trial: number | null
  fleet_earnings: number | null
  /** Of fleet earnings, what was paid on the fleet's other profiles. */
  fleet_other: number | null
  replies: number | null
  replies_share: number | null
  /** Trial links: OnlyFans' claim count (over the range, like clicks), its cap (null = none), days, expiry, finished. */
  claims: number | null
  claims_partial?: boolean
  claims_cap: number | null
  trial_days: number | null
  expires_at: string | null
  is_finished: boolean
  claimers_read_at: string | null
  watched_since: string | null
}

export interface LinkRow extends LinkPerformance {
  campaign_code: number | null
  trial_code: string | null
  subscribers: number | null
  live_reports: number
  claimers_error: string | null
  synced_at: string | null
}

export interface LinkTotals {
  clicks: number
  clicks_partial: boolean
  fans: number | null
  fans_backlog: number
  earnings: number | null
  cost: number
  complete: boolean
  spenders: number | null
  paid_after_trial: number | null
  replies: number | null
  claims: number | null
  claims_partial?: boolean
  profit: number | null
  cpf: number | null
  arpf: number | null
  roi: number | null
  cpc: number | null
  epc: number | null
  conversion: number | null
}

export interface SyncState {
  account_id: string
  last_synced_at: string | null
  last_error: string | null
  requested_at: string | null
  trial_claims_read_at?: string | null
  trial_claims_read_through?: string | null
  trial_claims_error?: string | null
}

/** What the list can be sorted by (server-side). */
export type SortKey =
  | 'created_at' | 'name' | 'profile' | 'clicks' | 'claims' | 'fans' | 'conversion' | 'replies' | 'spenders' | 'paid_after_trial'
  | 'cost' | 'earnings' | 'fleet_earnings' | 'profit' | 'cpc' | 'epc' | 'cpf' | 'arpf' | 'roi'

export interface ListQuery {
  kind: LinkKind
  accountId?: string
  search?: string
  includeDeleted?: boolean
  /** true = the archive; 'any' = both (pickers); otherwise the links in use. */
  archived?: boolean | 'any'
  range?: Range
  earning: Earning
  sort?: SortKey
  dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export interface ListResult {
  links: LinkRow[]
  total: number
  totals: LinkTotals | null
  sync: SyncState[]
  /** Replies are known from this day (when incoming messages began to be recorded). */
  replies_since?: string
}

export interface CostPayment {
  id: string
  amount: number
  paid_on: string
  note: string | null
}

export interface DailyPoint {
  day: string
  earnings: number
  fans: number
}

export interface LinkDetail {
  link: LinkRow
  costs: CostPayment[]
  daily: DailyPoint[]
  reading: { requested: boolean }
  range: { from: string | null; to: string | null }
  earning: Earning
}

/** In the order a report shows them; `chart` and `payers` are page features, not numbers. */
export const REPORT_COLUMNS = ['clicks', 'claims', 'fans', 'cost', 'earnings', 'profit', 'cpf', 'arpf', 'spenders', 'replies', 'roi', 'chart', 'payers'] as const
export type ReportColumn = (typeof REPORT_COLUMNS)[number]
/** A new report starts with everything but the Payer breakdown. */
export const DEFAULT_REPORT_COLUMNS: ReportColumn[] = REPORT_COLUMNS.filter((c) => c !== 'payers')

export interface LinkRef {
  account_id: string
  kind: LinkKind
  link_id: string
}

export interface LinkReport {
  id: string
  token: string
  name: string
  columns: ReportColumn[]
  expires_at: string | null
  expired: boolean
  created_at: string
  links: LinkRef[]
}

export interface ReportDraft {
  name: string
  links: LinkRef[]
  columns: ReportColumn[]
  expires_at: string | null
}

export interface Range {
  from: string | null
  to: string | null
}

export interface TrialDraft {
  name: string
  trialDays: number
  claimsCap: number | null
  cost: { amount: number } | null
}
